import React, { Suspense } from 'react';
import {
  generatePath,
  Navigate,
  Routes as ReactRouterRoutes,
  Route,
} from 'react-router-dom';

import { PERMISSIONS, ROLES } from '@savgroup-front-common/constants';
import { buildUrl } from '@savgroup-front-common/core/helpers';
import { retryImportChunk } from '@savgroup-front-common/core/helpers/retryImportChunk';
import {
  useHasPermission,
  useHasSomeRoles,
  useIsSuperAdmin,
  useSearchParams,
} from '@savgroup-front-common/core/hooks';
import BaseLoader from '@savgroup-front-common/core/molecules/BaseLoader/BaseLoader';
import { PRICING_PLAN, PRICING_PLAN_STATE } from '@savgroup-front-common/types';

import { BASES, ROUTES, SPARE_PART_ROUTES } from '../../../constants/routing';
import useHasBusinessIntelligencePermission from '../../../hooks/useHasBusinessIntelligencePermission';
import { ChoosePlanDrawer } from '../../components/ChoosePlanDrawer/ChoosePlanDrawer';
import { useAppContext } from '../App.context';

import useEnvFavicon from './hooks/useEnvFavicon';
import useHasSellerSettingsPagePermission from './hooks/useHasSellerSettingsPagePermission';
import messages from './messages';

const ShippingBoRedirectRoutes = React.lazy(() =>
  retryImportChunk(() => import('./ShippingBoRedirectRoutes')),
);

const NewSettingsPage = React.lazy(() =>
  retryImportChunk(() => import('../../pages/NewSettings')),
);

const IssueAQuotationPage = React.lazy(() =>
  retryImportChunk(() => import('../../pages/IssueAQuotationPage')),
);

const OrderPageNewUi = React.lazy(() =>
  retryImportChunk(() => import('../../pages/OrderPageNewUi/OrderPageNewUi')),
);

const BusinessIntelligencePage = React.lazy(() =>
  retryImportChunk(() => import('../../pages/BusinessIntelligencePage')),
);

const BillingPage = React.lazy(() =>
  retryImportChunk(() => import('../../pages/BillingPage')),
);

const NewCreateFilePage = React.lazy(() =>
  retryImportChunk(() => import('../../pages/NewCreateFile/NewCreateFile')),
);

const DebugInspectorPage = React.lazy(() =>
  retryImportChunk(() => import('../../pages/DebugInspectorPage')),
);
const WelcomePage = React.lazy(() =>
  retryImportChunk(
    () => import('../../pages/Onboarding/WelcomePage/WelcomePage'),
  ),
);

const FromScratch = React.lazy(() =>
  retryImportChunk(
    () => import('../../pages/Onboarding/FromScratch/FromScratch'),
  ),
);

const RequestsToValidatePage = React.lazy(() =>
  retryImportChunk(
    () =>
      import('../../pages/sparePart/RequestsToValidatePage/RequestsToValidatePage'),
  ),
);
const CreateSparePartRequestFromFilePage = React.lazy(() =>
  retryImportChunk(
    () =>
      import('../../pages/sparePart/CreateSparePartRequestFromFilePage/CreateSparePartRequestFromFilePage'),
  ),
);

const CreateSparePartRequestPage = React.lazy(() =>
  retryImportChunk(
    () =>
      import('../../pages/sparePart/CreateSparePartRequestPage/CreateSparePartRequestPage'),
  ),
);

const HomeInterventionPage = React.lazy(() =>
  retryImportChunk(
    () => import('../../pages/HomeIntervention/HomeInterventionPage'),
  ),
);

const InterventionReportPage = React.lazy(() =>
  retryImportChunk(
    () => import('../../pages/InterventionReportPage/InterventionReportPage'),
  ),
);

const FilesPageNewUI = React.lazy(() =>
  retryImportChunk(() => import('../../pages/FilesPageNewUI/FilesPage')),
);

const TransportPage = React.lazy(() =>
  retryImportChunk(() => import('../../pages/TransportPage/TransportPage')),
);

const JobsPage = React.lazy(() =>
  retryImportChunk(() => import('../../pages/JobsPage/JobsPage')),
);

const Routes = () => {
  const { selectedSeller } = useAppContext();

  useEnvFavicon();

  const hasBusinessIntelligencePermission =
    useHasBusinessIntelligencePermission();

  const hasInspectorAccessRights = useHasPermission(
    PERMISSIONS.INSPECTOR_ACCESS_RIGHTS,
  );

  const hasSellerSettingsPermissions = useHasSellerSettingsPagePermission();

  const hasUserManagementPermission = useHasPermission(
    PERMISSIONS.ROLE_MANAGEMENT,
  );
  const haseSanityTestsSettingsPermission = useHasPermission(
    PERMISSIONS.SANITY_TESTS,
  );
  const hasImportsPermission = useHasPermission(PERMISSIONS.IMPORT_MANAGEMENT);
  const hasConsoleAccessRightsPermission = useHasPermission(
    PERMISSIONS.CONSOLE_ACCESS_RIGHTS,
  );
  const isAdminDemo = useHasSomeRoles([
    ROLES.DEMO_ADMINISTRATOR,
    ROLES.ADMINISTRATOR,
  ]);
  const isSuperAdmin = useIsSuperAdmin();

  const hasManageSparePartCatalog = useHasPermission(
    PERMISSIONS.MANAGE_SPARE_PART_CATALOG,
  );

  const hasSomeSettingsPermissions =
    isAdminDemo ||
    hasSellerSettingsPermissions ||
    hasUserManagementPermission ||
    haseSanityTestsSettingsPermission ||
    hasImportsPermission ||
    hasConsoleAccessRightsPermission ||
    hasManageSparePartCatalog;

  const isFromScratchSeller =
    selectedSeller?.pricingPlanState !== PRICING_PLAN_STATE.ACTIVE &&
    (selectedSeller?.pricingPlan === PRICING_PLAN.FREEMIUM ||
      selectedSeller?.pricingPlan === PRICING_PLAN.NONE);

  const isBasicPricingPlan = selectedSeller?.pricingPlan === PRICING_PLAN.NONE;

  const query = useSearchParams();
  const code = query.get('code') || '';

  if (isFromScratchSeller && !isSuperAdmin) {
    return (
      <ReactRouterRoutes>
        <Route
          path={`${BASES.FROM_SCRATCH}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <FromScratch />
            </Suspense>
          }
        />
        <Route
          path={'*'}
          element={
            <Navigate
              to={buildUrl(generatePath(ROUTES.FROM_SCRATCH), { code })}
            />
          }
        />
      </ReactRouterRoutes>
    );
  }

  return (
    <>
      <ChoosePlanDrawer />

      <ReactRouterRoutes>
        {hasSomeSettingsPermissions && (
          <Route
            path={`${BASES.SETTINGS}/*`}
            element={
              <Suspense
                fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
              >
                <NewSettingsPage />
              </Suspense>
            }
          />
        )}

        <Route
          path={`${BASES.TODOS}/*`}
          element={<Navigate to={ROUTES.TODOS_NEW_UI} />}
        />
        <Route
          path={`${BASES.ALERTS}/*`}
          element={<Navigate to={ROUTES.TODOS_NEW_UI} />}
        />

        <Route
          path={`${ROUTES.FILES_NEW_UI}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <FilesPageNewUI />
            </Suspense>
          }
        />

        <Route
          path={`${BASES.ORDERS}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <OrderPageNewUi />
            </Suspense>
          }
        />

        <Route
          path={`${BASES.TRANSPORT}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <TransportPage />
            </Suspense>
          }
        />

        {hasSomeSettingsPermissions && (
          <>
            <Route
              path={`${BASES.SETTINGS}/*`}
              element={
                <Suspense
                  fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
                >
                  <NewSettingsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.SHIPPING_BO}
              element={
                <Suspense
                  fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
                >
                  <ShippingBoRedirectRoutes
                    isFromScratchSeller={isFromScratchSeller}
                  />
                </Suspense>
              }
            />
          </>
        )}

        {(isBasicPricingPlan || isSuperAdmin) && (
          <Route
            path={ROUTES.WELCOME_PAGE}
            element={
              <Suspense
                fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
              >
                <WelcomePage />
              </Suspense>
            }
          />
        )}
        {isSuperAdmin && (
          <Route
            path={`${ROUTES.FROM_SCRATCH}/*`}
            element={
              <Suspense
                fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
              >
                <FromScratch />
              </Suspense>
            }
          />
        )}

        <Route
          path={`${BASES.SPARE_PARTS}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <RequestsToValidatePage />
            </Suspense>
          }
        />

        <Route
          path={SPARE_PART_ROUTES.CREATE_REQUEST_FROM_FILE}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <CreateSparePartRequestFromFilePage />
            </Suspense>
          }
        />
        <Route
          path={SPARE_PART_ROUTES.CREATE_REQUEST}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <CreateSparePartRequestPage />
            </Suspense>
          }
        />

        <Route
          path={`${BASES.HOME_INTERVENTION}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <HomeInterventionPage />
            </Suspense>
          }
        />

        <Route
          path={`${BASES.INTERVENTION_REPORT}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <InterventionReportPage />
            </Suspense>
          }
        />

        <Route
          path={`${BASES.NEW_CREATE_FILE}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <NewCreateFilePage />
            </Suspense>
          }
        />

        <Route
          path={`${ROUTES.JOBS}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <JobsPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.ISSUE_A_QUOTATION}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <IssueAQuotationPage />
            </Suspense>
          }
        />

        <Route
          path={`${BASES.BILLING}/*`}
          element={
            <Suspense
              fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
            >
              <BillingPage />
            </Suspense>
          }
        />

        {hasBusinessIntelligencePermission && (
          <Route
            path={ROUTES.BUSINESS_INTELLIGENCE}
            element={
              <Suspense
                fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
              >
                <BusinessIntelligencePage />
              </Suspense>
            }
          />
        )}
        {hasInspectorAccessRights && (
          <Route
            path={ROUTES.DEBUG_INSPECTOR}
            element={
              <Suspense
                fallback={<BaseLoader messageList={[messages.pleaseWait]} />}
              >
                <DebugInspectorPage />
              </Suspense>
            }
          />
        )}

        <Route
          path={ROUTES.HOME}
          element={
            <Navigate
              to={(() => {
                if (!isBasicPricingPlan) {
                  return generatePath(ROUTES.FILES_NEW_UI);
                }

                return generatePath(ROUTES.WELCOME_PAGE);
              })()}
            />
          }
        />

        <Route path={'*'} element={<Navigate to={ROUTES.HOME} />} />
      </ReactRouterRoutes>
    </>
  );
};

export default Routes;
